import { Button, ButtonProps, Text } from "@chakra-ui/react";
import { FC, useState } from "react";
import writeXlsxFile from "write-excel-file";

import { AddIcon } from "@nens/ranalib";
import * as api from "@nens/threedi/openapi-client";

import ExportLoadingModal from "./ExportLoadingModal";
import { SchematisationRow } from "./SchematisationsList";

interface SchematisationRowsExcelButtonProps extends ButtonProps {
  schematisationsQuery: (limit: number, offset: number) => Promise<api.PaginatedSchematisationList>;
  organisationUniqueId?: string;
}

const excelFileSchema = [
  {
    column: "Schematisation name",
    value: (row: SchematisationRow) => row.name,
    width: 80,
  },
  {
    column: "uuid",
    value: (row: SchematisationRow) => row.uuid,
    width: 40,
  },
  {
    column: "Revision",
    value: (row: SchematisationRow) => row.latestRevisionNr ?? "-",
    width: 80,
  },
  {
    column: "Tags",
    value: (row: SchematisationRow) => row.tags,
    width: 20,
  },
  {
    column: "Updated",
    value: (row: SchematisationRow) => (row.lastUpdated ? new Date(row.lastUpdated) : null),
    type: Date,
    width: 15,
  },
  {
    column: "Created by",
    value: (row: SchematisationRow) => row.createdBy,
    width: 10,
  },
];

const SchematisationsExcelButton: FC<SchematisationRowsExcelButtonProps> = ({
  disabled,
  organisationUniqueId,
  schematisationsQuery,
  ...rest
}) => {
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);

  let fetchedAllSchematisations = false;
  const schematisationsRows: SchematisationRow[] = [];
  const limit = 250;
  let offset = 0;

  let schematisation: api.PaginatedSchematisationList | undefined;
  const fetchAllSchemasRows = async () => {
    while (!fetchedAllSchematisations) {
      schematisation = await schematisationsQuery(limit, offset);

      schematisation.results.map((row) => schematisationsRows.push({ ...row, uuid: "" + row.id }));
      schematisationsRows.push();

      if (schematisation.next) {
        offset = offset + schematisation.results.length;
        setProgress((offset / schematisation.count) * 100);
        await new Promise((r) => setTimeout(r, 1000));
      } else {
        setProgress(0);
        fetchedAllSchematisations = true;
      }
    }
    return schematisationsRows;
  };

  const onClick = async () => {
    setExporting(true);
    const rows = await fetchAllSchemasRows();

    // @ts-ignore  Sadly write-excel-file's typescript doesn't handle the case of multiple schemas
    await writeXlsxFile([rows], {
      fileName: "HCC schematisations overview.xlsx",
      schema: [excelFileSchema],
      fontFamily: "Montserrat", // Like the site
      dateFormat: "yyyy-mm-dd hh:mm",
      stickyRowsCount: 1, // Header
      sheets: ["Schematisations"],
    });
    setExporting(false);
  };

  return (
    <Button
      variant="primary"
      title="Export to Excel file"
      leftIcon={<AddIcon boxSize={5} />}
      onClick={onClick}
      disabled={disabled || exporting}
      {...rest}
    >
      <Text overflow="hidden" textOverflow="ellipsis" w="100%" m="0">
        Export to Excel file
      </Text>

      {exporting ? <ExportLoadingModal progress={progress} isOpen={exporting} /> : null}
    </Button>
  );
};

export default SchematisationsExcelButton;
